import React, { useState, useEffect } from 'react';
import { listDriveFolders, createDriveFolder } from '../services/googleDriveClient';
import {
    FolderIcon,
    ChevronRightIcon,
    ArrowLeftIcon,
    PlusIcon,
    CheckIcon,
    XMarkIcon
} from './icons';

interface DriveFolder {
    id: string;
    name: string;
}

interface DriveFolderPickerProps {
    initialFolderId?: string;
    onSelect: (folderId: string, folderName: string) => void;
    onCancel: () => void;
}

export const DriveFolderPicker: React.FC<DriveFolderPickerProps> = ({
    initialFolderId,
    onSelect,
    onCancel
}) => {
    const [currentPath, setCurrentPath] = useState<DriveFolder[]>([{ id: 'root', name: 'My Drive' }]);
    const [folders, setFolders] = useState<DriveFolder[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [isCreatingFolder, setIsCreatingFolder] = useState(false);

    const currentFolder = currentPath[currentPath.length - 1];

    useEffect(() => {
        loadFolders(currentFolder.id === 'root' ? undefined : currentFolder.id);
    }, [currentFolder]);

    const loadFolders = async (parentId?: string) => {
        setIsLoading(true);
        try {
            const data = await listDriveFolders(parentId);
            setFolders(data);
        } catch (error) {
            console.error('Failed to load folders:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleNavigate = (folder: DriveFolder) => {
        setCurrentPath([...currentPath, folder]);
    };

    const handleNavigateUp = () => {
        if (currentPath.length > 1) {
            setCurrentPath(currentPath.slice(0, -1));
        }
    };

    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) return;

        setIsLoading(true);
        try {
            const parentId = currentFolder.id === 'root' ? undefined : currentFolder.id;
            const newFolder = await createDriveFolder(newFolderName, parentId);

            if (newFolder) {
                setFolders([...folders, newFolder]);
                setNewFolderName('');
                setIsCreatingFolder(false);
            }
        } catch (error) {
            console.error('Failed to create folder:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-[#1f1f1f] border border-gray-700 rounded-xl overflow-hidden flex flex-col h-[500px] w-full max-w-2xl mx-auto shadow-2xl">
            {/* Header */}
            <div className="p-4 border-b border-gray-700 bg-[#252526] flex items-center justify-between">
                <div className="flex items-center gap-3">
                    {currentPath.length > 1 && (
                        <button
                            onClick={handleNavigateUp}
                            className="p-1 hover:bg-gray-700 rounded-full transition-colors"
                        >
                            <ArrowLeftIcon className="w-5 h-5 text-gray-400" />
                        </button>
                    )}
                    <h3 className="font-bold text-white text-lg flex items-center gap-2">
                        <FolderIcon className="w-5 h-5 text-blue-400" />
                        {currentFolder.name}
                    </h3>
                </div>
                <button
                    onClick={onCancel}
                    className="p-1 hover:bg-gray-700 rounded-full transition-colors"
                >
                    <XMarkIcon className="w-5 h-5 text-gray-400" />
                </button>
            </div>

            {/* Folder List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#18181b]">
                {/* Create New Folder Row */}
                {isCreatingFolder ? (
                    <div className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg border border-blue-500/50">
                        <FolderIcon className="w-6 h-6 text-blue-400" />
                        <input
                            autoFocus
                            type="text"
                            value={newFolderName}
                            onChange={(e) => setNewFolderName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleCreateFolder();
                                if (e.key === 'Escape') setIsCreatingFolder(false);
                            }}
                            placeholder="Folder Name"
                            className="flex-1 bg-transparent border-none focus:outline-none text-white text-sm"
                        />
                        <button onClick={handleCreateFolder} className="text-green-400 hover:text-green-300">
                            <CheckIcon className="w-5 h-5" />
                        </button>
                        <button onClick={() => setIsCreatingFolder(false)} className="text-red-400 hover:text-red-300">
                            <XMarkIcon className="w-5 h-5" />
                        </button>
                    </div>
                ) : (
                    <button
                        onClick={() => setIsCreatingFolder(true)}
                        className="w-full flex items-center gap-3 p-3 hover:bg-gray-800 rounded-lg border border-transparent hover:border-gray-700 transition-colors text-gray-400 hover:text-white"
                    >
                        <div className="w-6 h-6 rounded bg-gray-700 flex items-center justify-center">
                            <PlusIcon className="w-4 h-4" />
                        </div>
                        <span className="text-sm font-medium">Nouveau Dossier</span>
                    </button>
                )}

                {isLoading && !isCreatingFolder ? (
                    <div className="flex justify-center p-8">
                        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : (
                    folders.map(folder => (
                        <div
                            key={folder.id}
                            className="group flex items-center justify-between p-3 hover:bg-gray-800 rounded-lg border border-transparent hover:border-gray-700 transition-colors cursor-pointer"
                            onClick={() => handleNavigate(folder)}
                        >
                            <div className="flex items-center gap-3">
                                <FolderIcon className="w-6 h-6 text-gray-400 group-hover:text-blue-400 transition-colors" />
                                <span className="text-gray-200 text-sm font-medium">{folder.name}</span>
                            </div>
                            <ChevronRightIcon className="w-4 h-4 text-gray-600 group-hover:text-gray-400" />
                        </div>
                    ))
                )}

                {folders.length === 0 && !isLoading && (
                    <div className="text-center text-gray-500 py-10 text-sm">
                        Dossier vide
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-700 bg-[#252526] flex justify-end gap-3 glass">
                <button
                    onClick={onCancel}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
                >
                    Annuler
                </button>
                <button
                    onClick={() => onSelect(currentFolder.id === 'root' ? '' : currentFolder.id, currentFolder.name)}
                    className="px-4 py-2 rounded-lg text-sm font-bold bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/20 transition-all flex items-center gap-2"
                >
                    <CheckIcon className="w-4 h-4" />
                    Sélectionner "{currentFolder.name}"
                </button>
            </div>
        </div>
    );
};
